import { Copy, Plus, ShieldAlert, ShieldCheck, Trash2 } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  WebhookAuditPanel,
  WebhookAuditSummary,
  isWebhookAuditEvent,
  latestWebhookEventForConfig,
} from "@/components/webhooks/webhook-audit-panel";
import { WebhookTemplateSection } from "@/components/webhooks/webhook-template-section";
import { useOperatorStatus } from "@/hooks/useOperatorStatus";
import {
  useCreateWebhookConfig,
  useDeleteWebhookConfig,
  useUpdateWebhookConfig,
  useWebhookConfigs,
  useWebhookProviderSpecs,
  useWebhookStatuses,
  useWebhookTemplates,
} from "@/hooks/useWebhooks";
import type {
  WebhookConfig,
  WebhookConfigCreateRequest,
  WebhookProviderSpec,
  SignalAuditEvent,
  WebhookTemplate,
} from "@/lib/api";
import { cn } from "@/lib/utils";

const PROVIDERS = ["generic", "tradingview", "tradeideas", "trendspider"];

export default function OperatorWebhooksPage() {
  const configsQ = useWebhookConfigs();
  const providerSpecsQ = useWebhookProviderSpecs();
  const statusesQ = useWebhookStatuses();
  const templatesQ = useWebhookTemplates();
  const operatorStatusQ = useOperatorStatus();
  const [editing, setEditing] = useState<WebhookConfig | null>(null);
  const [creating, setCreating] = useState(false);
  const statusById = useMemo(
    () => new Map((statusesQ.data ?? []).map((status) => [status.source_id, status])),
    [statusesQ.data],
  );
  const webhookAuditEvents = useMemo(
    () => (operatorStatusQ.data?.recent_audit_events ?? []).filter(isWebhookAuditEvent),
    [operatorStatusQ.data?.recent_audit_events],
  );

  return (
    <div className="flex h-full flex-col">
      <header className="border-b border-border px-6 py-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold">Webhooks</h1>
            <p className="text-xs text-muted-fg">
              Push-based signal intake. Separate from browser scraping.
            </p>
          </div>
          <Button
            size="sm"
            onClick={() => {
              setCreating(true);
              setEditing(null);
            }}
          >
            <Plus />
            New webhook
          </Button>
        </div>
      </header>

      <main className="flex-1 overflow-auto p-6">
        {configsQ.isLoading ? (
          <div className="text-sm text-muted-fg">Loading webhooks...</div>
        ) : configsQ.isError ? (
          <div className="text-sm text-red-300">
            Failed to load webhooks: {(configsQ.error as Error).message}
          </div>
        ) : (
          <div className="grid gap-3 xl:grid-cols-2">
            {creating ? (
              <WebhookForm
                initial={null}
                providerSpecs={providerSpecsQ.data ?? []}
                templates={templatesQ.data ?? []}
                onCancel={() => setCreating(false)}
                onSaved={() => setCreating(false)}
              />
            ) : null}
            {(configsQ.data ?? []).map((config) =>
              editing?.id === config.id ? (
                <WebhookForm
                  key={config.id}
                  initial={config}
                  providerSpecs={providerSpecsQ.data ?? []}
                  templates={templatesQ.data ?? []}
                  onCancel={() => setEditing(null)}
                  onSaved={() => setEditing(null)}
                />
              ) : (
                <WebhookCard
                  key={config.id}
                  config={config}
                  templates={templatesQ.data ?? []}
                  secretAvailable={statusById.get(config.id)?.secret_available ?? false}
                  secretConfigured={statusById.get(config.id)?.secret_configured ?? false}
                  onEdit={() => {
                    setEditing(config);
                    setCreating(false);
                  }}
                  lastAuditEvent={latestWebhookEventForConfig(webhookAuditEvents, config.id)}
                />
              ),
            )}
            {!creating && (configsQ.data ?? []).length === 0 ? <EmptyState /> : null}
          </div>
        )}
        <WebhookAuditPanel events={webhookAuditEvents.slice(0, 12)} />
        <WebhookTemplateSection templates={templatesQ.data ?? []} loading={templatesQ.isLoading} />
      </main>
    </div>
  );
}

function EmptyState() {
  return (
    <section className="rounded-md border border-border bg-muted/10 p-5 text-sm xl:col-span-2">
      <div className="font-medium text-fg">No webhook configs yet.</div>
      <p className="mt-1 max-w-2xl text-xs leading-5 text-muted-fg">
        Add a TradingView, TradeIdeas, TrendSpider, or generic webhook here. The endpoint will
        appear after the config is saved.
      </p>
    </section>
  );
}

function WebhookCard({
  config,
  templates,
  secretConfigured,
  secretAvailable,
  onEdit,
  lastAuditEvent,
}: {
  config: WebhookConfig;
  templates: WebhookTemplate[];
  secretConfigured: boolean;
  secretAvailable: boolean;
  onEdit: () => void;
  lastAuditEvent: SignalAuditEvent | undefined;
}) {
  const del = useDeleteWebhookConfig();
  const endpoint = absoluteUrl(config.endpoint_path);
  const providerEndpoint = absoluteUrl(config.provider_endpoint_path);
  const defaultTemplate = templates.find((template) => template.id === config.default_template_id);
  return (
    <section className="rounded-md border border-border bg-muted/5 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="truncate text-sm font-semibold">{config.display_name}</h2>
            <code className="text-[11px] text-muted-fg">{config.id}</code>
            <Badge variant="outline" className="text-[10px]">{config.provider}</Badge>
            <Badge variant={config.enabled ? "success" : "outline"} className="text-[10px]">
              {config.enabled ? "active" : "inactive"}
            </Badge>
          </div>
          <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-muted-fg">
            {config.vendor_id ? <span>vendor: <span className="text-fg">{config.vendor_id}</span></span> : null}
            {defaultTemplate ? <span>template: <span className="text-fg">{defaultTemplate.display_name}</span></span> : null}
            <SecretState configured={secretConfigured} available={secretAvailable} />
            <WebhookAuditSummary event={lastAuditEvent} />
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="h-8 px-2.5 text-xs" onClick={onEdit}>
            Edit
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="size-8 text-muted-fg"
            title="Delete webhook"
            disabled={del.isPending}
            onClick={() => {
              if (!confirm(`Delete webhook '${config.id}'? Captured inbox rows stay in the DB.`)) return;
              del.mutate(config.id, {
                onSuccess: () => toast.success(`deleted ${config.id}`),
                onError: (err) => toast.error(`delete failed: ${err.message}`),
              });
            }}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      </div>

      <EndpointRow label="Endpoint" value={endpoint} />
      <EndpointRow label="Provider endpoint" value={providerEndpoint} />
      {config.provider === "tradingview" ? (
        <p className="mt-3 rounded-md bg-muted/20 px-3 py-2 text-xs leading-5 text-muted-fg">
          TradingView JSON alerts may include <code>{"\"secret\":\"...\""}</code> or{" "}
          <code>{"\"token\":\"...\""}</code>. The auth field is removed before inbox persistence.
        </p>
      ) : null}
    </section>
  );
}

function WebhookForm({
  initial,
  providerSpecs,
  templates,
  onCancel,
  onSaved,
}: {
  initial: WebhookConfig | null;
  providerSpecs: WebhookProviderSpec[];
  templates: WebhookTemplate[];
  onCancel: () => void;
  onSaved: () => void;
}) {
  const create = useCreateWebhookConfig();
  const update = useUpdateWebhookConfig();
  const [form, setForm] = useState({
    id: initial?.id ?? "",
    display_name: initial?.display_name ?? "",
    provider: initial?.provider ?? "generic",
    vendor_id: initial?.vendor_id ?? "",
    default_template_id: initial?.default_template_id ?? "",
    secret_env: typeof initial?.auth_config.secret_env === "string" ? initial.auth_config.secret_env : "",
    enabled: initial?.enabled ?? true,
  });
  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((current) => ({ ...current, [key]: value }));
  const providerSpec = providerSpecs.find((spec) => spec.provider === form.provider);
  const providerTemplates = templates.filter((template) => template.provider === form.provider);

  const submit = () => {
    const body: WebhookConfigCreateRequest = {
      id: form.id,
      display_name: form.display_name,
      provider: form.provider,
      vendor_id: form.vendor_id || null,
      enabled: form.enabled,
      default_template_id: form.default_template_id || null,
      allowed_template_ids: form.default_template_id ? [form.default_template_id] : [],
      auth_config: form.secret_env ? { secret_env: form.secret_env } : {},
      parser_config: {},
      metadata: {},
    };
    if (initial) {
      update.mutate(
        { id: initial.id, patch: body },
        {
          onSuccess: () => {
            toast.success(`saved ${initial.id}`);
            onSaved();
          },
          onError: (err) => toast.error(`save failed: ${err.message}`),
        },
      );
    } else {
      create.mutate(body, {
        onSuccess: () => {
          toast.success(`created ${form.id}`);
          onSaved();
        },
        onError: (err) => toast.error(`create failed: ${err.message}`),
      });
    }
  };

  const pending = create.isPending || update.isPending;
  return (
    <section className="rounded-md border border-border bg-muted/10 p-4">
      <h2 className="text-sm font-semibold">{initial ? `Edit ${initial.id}` : "New webhook"}</h2>
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <Field label="id">
          <input className={inputClass} value={form.id} disabled={Boolean(initial)} onChange={(e) => set("id", e.target.value)} />
        </Field>
        <Field label="Display name">
          <input className={inputClass} value={form.display_name} onChange={(e) => set("display_name", e.target.value)} />
        </Field>
        <Field label="Provider">
          <select className={inputClass} value={form.provider} onChange={(e) => set("provider", e.target.value)}>
            {PROVIDERS.map((provider) => (
              <option key={provider} value={provider}>{provider}</option>
            ))}
          </select>
        </Field>
        <Field label="Vendor">
          <input className={inputClass} value={form.vendor_id} onChange={(e) => set("vendor_id", e.target.value)} />
        </Field>
        <Field label="Default template" className="md:col-span-2">
          <select
            className={inputClass}
            value={form.default_template_id}
            onChange={(e) => set("default_template_id", e.target.value)}
          >
            <option value="">Auto-detect from payload</option>
            {providerTemplates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.display_name} ({template.id})
              </option>
            ))}
          </select>
        </Field>
        <Field label="Secret env var" className="md:col-span-2">
          <input
            className={inputClass}
            value={form.secret_env}
            placeholder="TRADINGVIEW_WEBHOOK_SECRET"
            onChange={(e) => set("secret_env", e.target.value)}
          />
        </Field>
        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" checked={form.enabled} onChange={(e) => set("enabled", e.target.checked)} />
          Active
        </label>
      </div>
      <ProviderFormatPanel spec={providerSpec} />
      <div className="mt-4 flex justify-end gap-2">
        <Button size="sm" variant="outline" disabled={pending} onClick={onCancel}>Cancel</Button>
        <Button size="sm" disabled={pending} onClick={submit}>{initial ? "Save" : "Create"}</Button>
      </div>
    </section>
  );
}

function ProviderFormatPanel({ spec }: { spec: WebhookProviderSpec | undefined }) {
  if (!spec) {
    return (
      <div className="mt-4 rounded-md border border-border bg-bg px-3 py-2 text-xs text-muted-fg">
        No provider format metadata is available for this provider.
      </div>
    );
  }
  return (
    <section className="mt-4 rounded-md border border-border bg-bg p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-xs font-semibold text-fg">{spec.display_name} accepted formats</h3>
          <p className="mt-1 text-[11px] leading-4 text-muted-fg">{spec.auth_description}</p>
        </div>
      </div>
      <div className="mt-3 grid gap-2">
        {spec.formats.map((format) => (
          <div key={`${spec.provider}-${format.label}`} className="rounded-md border border-border/70 p-2">
            <div className="mb-1 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[11px] font-medium text-fg">{format.label}</div>
                <div className="text-[10px] text-muted-fg">{format.content_type}</div>
              </div>
              <Button
                size="icon"
                variant="ghost"
                className="size-7 shrink-0"
                title={`Copy ${format.label.toLowerCase()} template`}
                onClick={() => void copy(format.template)}
              >
                <Copy className="size-3.5" />
              </Button>
            </div>
            <p className="mb-2 text-[11px] leading-4 text-muted-fg">{format.description}</p>
            <code className="block max-h-24 overflow-auto rounded border border-border/70 bg-muted/10 px-2 py-1.5 text-[11px] leading-4 text-fg">
              {format.template}
            </code>
          </div>
        ))}
      </div>
    </section>
  );
}

function EndpointRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="mt-3">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-[11px] font-medium uppercase tracking-wider text-muted-fg">{label}</span>
        <Button
          size="icon"
          variant="ghost"
          className="size-7"
          title={`Copy ${label.toLowerCase()}`}
          onClick={() => void copy(value)}
        >
          <Copy className="size-3.5" />
        </Button>
      </div>
      <code className="block overflow-x-auto rounded-md border border-border bg-bg px-3 py-2 text-xs text-fg">
        {value}
      </code>
    </div>
  );
}

function SecretState({ configured, available }: { configured: boolean; available: boolean }) {
  return (
    <span className={cn("inline-flex items-center gap-1", available ? "text-emerald-300" : "text-amber-300")}>
      {available ? <ShieldCheck className="size-3.5" /> : <ShieldAlert className="size-3.5" />}
      {!configured ? "no secret ref" : available ? "secret available" : "env var missing"}
    </span>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <label className={cn("text-xs", className)}>
      <span className="mb-1 block text-muted-fg">{label}</span>
      {children}
    </label>
  );
}

async function copy(value: string) {
  await navigator.clipboard.writeText(value);
  toast.success("copied");
}

function absoluteUrl(path: string): string {
  return new URL(path, window.location.origin).toString();
}

const inputClass =
  "h-8 w-full rounded-md border border-border bg-bg px-2 text-xs text-fg outline-none focus:border-accent";
